import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentPermissionService } from '@/services/documentPermission.service';
import { resolveApiErrorMessage } from '@/utils/apiErrorMessages';
import type {
  Permission,
  PermissionEntity,
  PermissionEntityType,
  PermissionRightKey,
  PermissionRights,
  PermissionUser,
} from '@/types/permissions';

const DEFAULT_RIGHTS: PermissionRights = {
  canRead: true,
  canWrite: false,
  canDelete: false,
  canShare: false,
};

export const entityLabel = (entity: PermissionEntity, type: PermissionEntityType): string =>
  type === 'user'
    ? `${(entity as PermissionUser).name} (${(entity as PermissionUser).email})`
    : (entity as { name: string }).name;

export function usePermissionManagement(documentId?: string) {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<PermissionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);
  const [saving, setSaving] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);

  const [selectedEntity, setSelectedEntity] = useState<PermissionEntity | null>(null);
  const [permissionRights, setPermissionRights] = useState<PermissionRights>(DEFAULT_RIGHTS);

  const loadData = useCallback(async () => {
    if (!documentId) {
      return;
    }

    const requestId = ++loadRequestIdRef.current;

    try {
      setLoading(true);
      setError(null);
      setActionError(null);
      const [permissionRows, tenantUsers] = await Promise.all([
        documentPermissionService.listDocumentPermissions(documentId),
        documentPermissionService.listTenantUsers(),
      ]);
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setPermissions(permissionRows);
      setUsers(tenantUsers);
    } catch (err) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setError(
        resolveApiErrorMessage(t, err, { fallbackKey: 'permissions.loadFailed' }),
      );
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message
  }, [documentId]);

  useEffect(() => {
    if (!documentId) {
      return;
    }
    void loadData();
  }, [documentId, loadData]);

  const openAddDialog = () => {
    setEditingPermission(null);
    setSelectedEntity(null);
    setPermissionRights(DEFAULT_RIGHTS);
    setDialogError(null);
    setAddDialogOpen(true);
  };

  const openEditDialog = (permission: Permission) => {
    setEditingPermission(permission);
    setPermissionRights({
      canRead: permission.canRead,
      canWrite: permission.canWrite,
      canDelete: permission.canDelete,
      canShare: permission.canShare,
    });
    setDialogError(null);
    setAddDialogOpen(true);
  };

  const deletePermission = async (permission: Permission) => {
    if (!documentId) {
      return;
    }

    try {
      await documentPermissionService.revokePermission(documentId, permission.id);
      setPermissions((prev) => prev.filter((entry) => entry.id !== permission.id));
    } catch (err) {
      setActionError(
        resolveApiErrorMessage(t, err, { fallbackKey: 'permissions.deleteFailed' }),
      );
    }
  };

  const savePermission = async () => {
    if (!documentId) {
      return;
    }

    setSaving(true);
    setDialogError(null);

    try {
      if (editingPermission) {
        const updated = await documentPermissionService.updatePermission(
          documentId,
          editingPermission.id,
          permissionRights,
        );
        setPermissions((prev) =>
          prev.map((entry) => (entry.id === editingPermission.id ? updated : entry)),
        );
      } else if (selectedEntity) {
        const created = await documentPermissionService.grantPermission(
          documentId,
          selectedEntity.id,
          permissionRights,
        );
        setPermissions((prev) => {
          const withoutDuplicate = prev.filter(
            (entry) => !(entry.type === 'user' && entry.targetId === selectedEntity.id),
          );
          return [...withoutDuplicate, created];
        });
      } else {
        return;
      }

      setAddDialogOpen(false);
      setEditingPermission(null);
      setSelectedEntity(null);
    } catch (err) {
      setDialogError(
        resolveApiErrorMessage(t, err, { fallbackKey: 'permissions.saveFailed' }),
      );
    } finally {
      setSaving(false);
    }
  };

  const closeDialog = () => {
    if (saving) {
      return;
    }
    setAddDialogOpen(false);
    setEditingPermission(null);
    setSelectedEntity(null);
    setDialogError(null);
  };

  const toggleRight = (right: PermissionRightKey) => (checked: boolean) => {
    setPermissionRights((prev) => ({ ...prev, [right]: checked }));
  };

  return {
    permissions,
    availableEntities: users,
    loading,
    error,
    actionError,
    dialogError,
    saving,
    addDialogOpen,
    editingPermission,
    selectedEntity,
    permissionRights,
    setSelectedEntity,
    toggleRight,
    openAddDialog,
    openEditDialog,
    deletePermission,
    savePermission,
    closeDialog,
  };
}
